import { redirect, LoaderFunctionArgs } from 'react-router-dom';
import { getRouteStorageKey, objectToQueryParams } from 'utils/helper';
import { generateTableKey } from 'hooks/useTableStorageKey';

export function restoreQueryLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const key = getRouteStorageKey(url.pathname);

  const params = new URLSearchParams(url.search);
  const hasSearch = !!url.search;
  const hasSortByInUrl = params.getAll('sortBy').length > 0;

  // If URL already has sort params, never override them
  if (hasSortByInUrl) {
    return null;
  }

  let parsed: Record<string, any> = {};
  if (!hasSearch) {
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        parsed = JSON.parse(stored);
      } catch {
        localStorage.removeItem(key);
      }
    }
  }

  // If no sortBy in the stored query params, fall back to the table layout's saved sort
  const hasSortBy = Array.isArray(parsed.sortBy) ? parsed.sortBy.length > 0 : !!parsed.sortBy;
  if (!hasSortBy) {
    const tableLayoutKey = generateTableKey(url.pathname);
    try {
      const savedLayout = JSON.parse(localStorage.getItem(tableLayoutKey) ?? 'null');
      if (savedLayout && Array.isArray(savedLayout.sortBy) && savedLayout.sortBy.length) {
        parsed.sortBy = savedLayout.sortBy.map((s: { id: string; desc: boolean }) => `${s.id}:${s.desc ? 'DESC' : 'ASC'}`);
      }
    } catch {}
  }

  // URL has existing params (filters) but no sort — merge stored sort into the current URL
  if (hasSearch && parsed.sortBy) {
    const newParams = new URLSearchParams(url.search);
    const sortValues: string[] = Array.isArray(parsed.sortBy) ? parsed.sortBy : [parsed.sortBy];
    sortValues.forEach((v) => newParams.append('sortBy', v));
    const newSearch = newParams.toString();
    if (url.search.replace(/^\?/, '') === newSearch) return null;
    return redirect(`${url.pathname}?${newSearch}`);
  }

  let search = objectToQueryParams(parsed);
  if (!search) return null;

  // prevent infinite redirect
  if (url.search.replace(/^\?/, '') === search) return null;

  return redirect(`${url.pathname}?${search}`);
}

export function restoreQueryShouldRevalidate({ currentUrl, nextUrl }: { currentUrl: URL; nextUrl: URL }) {
  return currentUrl.pathname !== nextUrl.pathname;
}
